/**
 * HomePage — marketing / landing page for unauthenticated users.
 *
 * Layout contract
 * - The header is the page bar every page has (`ns-pagebar`, 64px), held `fixed`
 *   here. `<main>` offsets with `pt-16` and the auth panel starts at `top-16`,
 *   so all three share one token.
 * - The hero's height is content-driven (no min-height). A `max-height:720px`
 *   variant trims padding and the studio window so nav + hero + CTAs fit a
 *   1366x640 laptop above the fold.
 * - Triggering auth slides a panel in from the right; `<main>` shifts left in
 *   sync, the studio window unmounts, and the hero shows feature pills instead
 *   of the CTA pair.
 *
 * What the page says (docs/DESIGN_SYSTEM.md, "Brand"; the A14 brand boards): tools for people
 * who care how it ends. On a film the maker's name is on the story and the studio's name is on
 * the tools, so the page reads like credits: written by you, directed by you, models on your own
 * keys, tools by Noodle Studio, nothing added on top. The example is the brand's own fiction
 * (public/brand: Pip the fox, Lantern Harbour, the writer Mia), never a user's work. Every claim
 * about plans comes from SUBSCRIPTION_LIMITS in types.ts; the Pro price from utils/pricing.ts.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Users, Paperclip, ArrowLeft, ArrowRight, Loader2, X, Check, FileText, KeyRound, Play, Send,
  type LucideIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { registerUser, loginUser, loginWithGoogle, resetPassword } from '../utils/storage';
import { UserProfile } from '../types';
import { usePageMeta, PAGE_META } from '../hooks/usePageMeta';
import { BetaTag, BrandMark } from '../components/BrandMark';
import { PRO_DISCOUNT_PERCENT, PRO_LIST_USDC, PRO_MONTHLY_USDC } from '../utils/pricing';
import { SAYINGS } from '../utils/sayings';
import type { NodeTone } from '../components/nodes/nodeTheme';

interface HomePageProps {
  onGetStarted: () => void;  // Legacy - now handled internally
  onSignIn: () => void;      // Legacy - now handled internally
  onLoginSuccess: (user: UserProfile, isNewSignup: boolean) => void;
  isFirebaseConfigured: boolean;
  onShowSubscriptionModal: () => void;
  /** Counts up each time the Pro dialog asks for the sign-in panel to open ("Sign in to pay") */
  openAuthRequest?: number;
  onTerms?: () => void;
  onPrivacy?: () => void;
  onAbout?: () => void;
}

// ── Static content ──────────────────────────────────────────────────────────

/** The one line */
export const TAGLINE = 'Tools for people who care how it ends.';

/** Every model named here is in models/config.ts; the last entry is the video models, named once */
const MODELS = ['GPT Image 1.5', 'Nano Banana Pro', 'Gemini 3 Pro Image', 'FLUX · local ComfyUI', 'Qwen 2.5 · on device', 'Veo 3.1 · Sora 2 · Seedance 2.5'];

/**
 * Shown in the hero while the auth panel is open (replaces the CTA pair). A mark takes the tone
 * of the node kind it stands for (Reference mint, Script lemon); what is no kind of node takes the brand's.
 */
const PANEL_PILLS: { icon: LucideIcon; tone?: NodeTone; label: string }[] = [
  { icon: Paperclip, tone: 'mint', label: 'One reference. The same face, every frame.' },
  { icon: FileText, tone: 'lemon', label: 'A screenplay page that paginates like a real one.' },
  { icon: KeyRound, label: 'Your own keys from Venice, Google or OpenAI. Nothing added on top.' },
  { icon: Users, label: 'Projects shared with your team.' },
];

/** The free plan's facts (types.ts SUBSCRIPTION_LIMITS): 1 workspace, 3 nodes, own keys, cloud sync on every tier */
const FREE_PLAN = ['1 workspace, 3 nodes', 'The Ideas board and the screenplay', 'Every model, with your own keys', 'Cloud sync, files saved to your own folder'];
const PRO_PLAN = ['Unlimited workspaces and nodes', '1 GB of cloud storage', 'Projects shared with your team', 'Everything in Free'];

/** The credits: the maker's name on the story, ours on the tools */
const CREDITS: [string, string][] = [
  ['Written by', 'You'],
  ['Directed by', 'You'],
  ['Characters', 'Yours, from one reference'],
  ['Pictures and video', 'Models of your choice, on your own keys'],
  ['Tools', 'Noodle Studio'],
  ['Added on top', 'Nothing'],
];

/** Three stills of the film, under the credits */
const STILLS = [
  { src: '/brand/shot-window.webp', alt: 'Pip at a round window at night with a brass lantern' },
  { src: '/brand/shot-pier.webp', alt: 'Pip on a pier at dusk holding the lantern' },
  { src: '/brand/shot-harbour.webp', alt: 'Lantern Harbour at dusk: a stone town on a hill, lanterns over mint water' },
];

/** Where a key comes from, and what it opens. Each is a provider the app takes a key from (Settings › Keys). */
const KEY_PROVIDERS = [
  { name: 'Venice', line: 'Claude, GPT, Gemini, Seedance and more through one key, paid from a balance you hold.' },
  { name: 'Google', line: 'Gemini for text and pictures, on your own Google account.' },
  { name: 'OpenAI', line: 'GPT and image models, billed to you by OpenAI.' },
];

/** Beta, said plainly */
const BETA_POINTS: [string, string][] = [
  ['Things change weekly', 'New tools land often. What you made keeps working.'],
  ['Some corners are rough', 'When something looks odd, it probably is. Tell us.'],
  ['Your work is yours', 'Files on your disk, synced when you ask; export any time.'],
];

/** What landed lately: true, and short */
const WHATS_NEW: [string, string][] = [
  ['Screenplay, opened to write', 'The page in the middle, a chat at the right; the model’s change shows on the page until you keep it.'],
  ['Reactions for viewers', 'Anyone a workspace is shared with can react to a node.'],
  ['Scene by scene', 'Asking for a change costs the tokens of the scenes that change, not the whole script.'],
];

// ── The pieces of one small film, in the product's own cards ────────────────

const IdeaCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`rounded-2xl bg-white border border-cream-300 shadow-soft overflow-hidden ${className}`}>
    <div className="flex items-center gap-2 h-9 px-2.5 bg-lavender-soft text-lavender-deep">
      <span className="w-[22px] h-[22px] shrink-0 rounded-full bg-lavender-deep text-white grid place-items-center text-2xs font-bold">M</span>
      <span className="text-xs font-bold truncate">The lantern never goes out</span>
    </div>
    <p className="px-3 pt-3 pb-2.5 font-serif text-[13px] leading-[1.5] text-ink">A small fox who cannot keep a lantern lit learns the harbour was never dark, only waiting for someone to notice.</p>
    <div className="flex items-center gap-1 px-3 pb-3 text-2xs font-semibold">
      <span className="px-1.5 py-0.5 rounded-full bg-cream-200">🔥 4</span>
      <span className="px-1.5 py-0.5 rounded-full bg-cream-200">❤️ 2</span>
      <span className="ml-auto px-2 py-0.5 rounded-full bg-mint-soft text-mint-deep font-bold">Kept</span>
    </div>
  </div>
);

const PAGE_LINES: [string, number, boolean?][] = [
  ['EXT. PIER - DUSK', 0, true], ['', 0], ['Pip balances the lantern on the rail to tie his boot. A gull lands beside it, head tilted the way Pip tilts his.', 0], ['', 0],
  ['The gull hops. The lantern rocks, once, twice.', 0], ['', 0], ['PIP', 22], ['Not again.', 10], ['', 0],
  ['The violet flame drops into the water and keeps burning under it, lighting the fish from below.', 0], ['', 0], ['MIA (O.S.)', 22], ['(from the steps)', 16], ['It is always the gull.', 10], ['', 0],
  ['Pip, flat on the boards, watches the glow drift out past the boats. He does not look sad. He looks like he has an idea.', 0],
];

/** The top of a screenplay page, in Courier, cropped to the height it is given */
const PageCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`relative overflow-hidden rounded bg-white border border-cream-300 shadow-soft ns-t-mono text-ink ${className}`} style={{ padding: '12% 12% 0 18%' }}>
    {/* ui-ok: a screenplay page in miniature, its type at the page's scale, not read as text */}
    <span className="absolute right-[12%] top-[6%]" style={{ fontSize: 9 }}>3.</span>
    {PAGE_LINES.map(([text, indent, bold], i) => (
      <p key={i} className={`leading-[1.3] whitespace-pre-wrap ${bold ? 'font-bold' : ''}`} style={{ fontSize: 8.5, marginLeft: `${indent * 1.6}%`, minHeight: '1.3em' }}>{text}</p>
    ))}
  </div>
);

const CutCard: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`rounded-2xl p-2.5 flex flex-col gap-1.5 bg-ink text-cream ${className}`}>
    <div className="flex items-center gap-2 text-2xs ns-t-mono text-cream/70">
      <span className="w-5 h-5 shrink-0 rounded-full grid place-items-center bg-cream text-ink"><Play size={12} fill="currentColor" aria-hidden="true" /></span>00:00:47 / 00:01:58
    </div>
    <div className="relative h-10 rounded-lg overflow-hidden flex gap-0.5">
      {['/brand/shot-harbour.webp', '/brand/shot-pier.webp', '/brand/fox-raincoat.webp', '/brand/shot-window.webp', '/brand/lantern.webp'].map(src => (
        <img key={src} src={src} alt="" className="flex-1 min-w-0 h-10 object-cover" loading="lazy" decoding="async" draggable={false} />
      ))}
      <span className="absolute top-0 bottom-0 w-0.5 bg-cream" style={{ left: '38%' }} />
    </div>
    <div className="h-4 rounded-md flex items-center gap-px px-1.5 overflow-hidden bg-mint-deep">
      {Array.from({ length: 60 }, (_, i) => <span key={i} className="w-0.5 rounded-sm bg-mint" style={{ height: `${4 + Math.round(8 * Math.abs(Math.sin(i * 0.7)))}px` }} />)}
    </div>
  </div>
);

const ShotsGrid: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`grid grid-cols-3 gap-1.5 ${className}`}>
    {['/brand/shot-harbour.webp', '/brand/shot-pier.webp', '/brand/shot-window.webp', '/brand/fox-raincoat.webp', '/brand/lantern.webp', '/brand/fox-base.webp'].map((src, i) => (
      <span key={src} className="relative block">
        <img src={src} alt="" className="w-full aspect-[16/9] object-cover rounded-lg" loading="lazy" decoding="async" draggable={false} />
        <span className="absolute left-1 top-1 w-4 h-4 rounded-full bg-white/90 text-ink text-2xs font-bold grid place-items-center">{i + 1}</span>
      </span>
    ))}
  </div>
);

const Picture: React.FC<{ src: string; label: string; className?: string }> = ({ src, label, className = '' }) => (
  <div aria-hidden="true" className={`relative ${className}`}>
    <img src={src} alt="" className="w-full aspect-[16/9] object-cover rounded-2xl" loading="lazy" decoding="async" draggable={false} />
    <span className="absolute left-2.5 bottom-2.5 inline-flex items-center h-6 px-2.5 rounded-full bg-white/90 text-2xs font-bold text-ink">{label}</span>
  </div>
);

/** The six rooms of one production */
const ROOMS: { name: string; line: string; visual: React.ReactNode }[] = [
  { name: 'Ideas', line: 'Think in cards. Keep the good ones.', visual: <IdeaCard /> },
  { name: 'Script', line: 'A page that paginates like a real one.', visual: <PageCard className="h-[156px]" /> },
  { name: 'Cast', line: 'One reference. The same face, every frame.', visual: <Picture src="/brand/turnaround.webp" label="Pip · reference" /> },
  { name: 'Boards', line: 'Frames you can shoot from.', visual: <Picture src="/brand/storyboard.webp" label="Storyboard · 12 frames" /> },
  { name: 'Shots', line: 'Any model you hold a key for.', visual: <ShotsGrid /> },
  { name: 'The cut', line: 'Trim it, score it, export it.', visual: <CutCard /> },
];

// ── The studio window: a real workspace, with the film's pieces in it ───────

const StudioWindow: React.FC = () => (
  <div
    role="img"
    aria-label="A Noodle Studio workspace: an idea card, a screenplay page, a character reference, a storyboard, six shots and the cut, side by side"
    className="ns-glass rounded-3xl p-3 lg:h-[480px] xl:h-[520px] [@media(max-height:720px)]:lg:h-[400px] transition-transform duration-300 ease-pop hover:-translate-y-1.5 flex flex-col"
  >
    {/* ui-ok: a bar that holds chips is a pill, not a circle */}
    <div aria-hidden="true" className="flex items-center gap-2 h-8 px-1 shrink-0">
      <span className="ns-chip ns-chip-sm bg-white border border-cream-300 text-ink font-bold pointer-events-none">Library</span>
      <span className="text-xs text-ink-soft font-medium truncate">Lantern Harbour › Episode 1</span>
      <span className="ml-auto ns-seg p-0.5 pointer-events-none">
        <span className="ns-seg-item" aria-pressed="true">Ideas</span>
        <span className="ns-seg-item">Nodes</span>
        <span className="ns-seg-item hidden sm:inline-flex">Edit</span>
      </span>
    </div>
    <div className="ns-dotgrid rounded-2xl flex-1 min-h-0 mt-1.5 p-3 grid grid-cols-2 md:grid-cols-[1fr_0.95fr_0.8fr] gap-3 overflow-hidden">
      <div className="flex flex-col gap-3 min-w-0">
        <IdeaCard />
        <Picture src="/brand/turnaround.webp" label="Pip · reference" />
      </div>
      <PageCard className="h-full min-h-[240px]" />
      <div className="hidden md:flex flex-col gap-3 min-w-0">
        <Picture src="/brand/storyboard.webp" label="Storyboard" />
        <ShotsGrid />
        <CutCard />
      </div>
    </div>
  </div>
);

// ── Page ────────────────────────────────────────────────────────────────────

export const HomePage: React.FC<HomePageProps> = ({
  onLoginSuccess,
  isFirebaseConfigured,
  onShowSubscriptionModal,
  openAuthRequest = 0,
  onTerms,
  onPrivacy,
  onAbout
}) => {
  // Auth panel state
  const [showAuthPanel, setShowAuthPanel] = useState(false);
  usePageMeta(PAGE_META.home);
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot'>('login');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // Focus management for the auth panel
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);

  // Below md the panel covers the whole page, so the content underneath is
  // made inert while it is open; from md up the hero stays visible beside it.
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  const handleShowAuth = (mode: 'login' | 'signup') => {
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAuthMode(mode);
    setAuthError('');
    setAuthSuccess('');
    setShowAuthPanel(true);
  };

  // The Pro dialog's "Sign in to pay" opens the panel on the login side
  useEffect(() => {
    if (openAuthRequest > 0) handleShowAuth('login');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAuthRequest]);

  const handleCloseAuth = () => {
    setShowAuthPanel(false);
    setAuthError('');
    setAuthSuccess('');
    setEmail('');
    setPassword('');
    setName('');
  };

  // Return focus to whichever button opened the panel. This runs after React
  // commits the close, because the hero CTAs are `invisible` while the panel
  // is open and a synchronous focus() on them would be a no-op.
  useEffect(() => {
    if (showAuthPanel || !openerRef.current) return;
    const opener = openerRef.current;
    openerRef.current = null;
    // Focus after the next paint so the hero CTA row has left `invisible`;
    // retry once after its 300ms transition in case a browser still reports
    // it hidden (visibility is not in that transition, but be defensive).
    let timer = 0;
    const raf = requestAnimationFrame(() => {
      opener.focus();
      if (document.activeElement !== opener) {
        timer = window.setTimeout(() => opener.focus(), 350);
      }
    });
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [showAuthPanel]);

  // Escape closes the panel; the first input receives focus once it has slid in.
  useEffect(() => {
    if (!showAuthPanel) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      handleCloseAuth();
    };
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector<HTMLElement>('input')?.focus();
    }, 300);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAuthPanel]);

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsAuthenticating(true);
    try {
      if (authMode === 'signup') {
        if (!name.trim()) throw new Error('Name is required');
        const newUser = await registerUser(name, email, password);
        if (newUser.verificationLink) {
          setVerificationLink(newUser.verificationLink);
          setAuthSuccess('Account created! Please verify your email:');
          setIsAuthenticating(false);
          // Still log them in after a short delay
          setTimeout(() => onLoginSuccess(newUser, true), 100);
        } else {
          onLoginSuccess(newUser, true);
        }
      } else {
        const loggedUser = await loginUser(email, password);
        onLoginSuccess(loggedUser, false);
      }
    } catch (err: any) {
      let msg = err.message || 'Authentication failed';
      if (msg.includes('auth/email-already-in-use')) msg = 'Email already registered. Please sign in instead.';
      if (msg.includes('auth/invalid-credential')) msg = 'Invalid email or password. Please check and try again.';
      if (msg.includes('auth/user-not-found')) msg = 'No account found with this email. Please sign up first.';
      if (msg.includes('user-not-found')) msg = 'No account found with this email. Please sign up first.';
      if (msg.includes('popup-closed-by-user')) msg = 'Google sign-in was cancelled. Please try again.';
      if (msg.includes('popup-blocked')) msg = 'Google sign-in popup was blocked. Please allow popups.';
      setAuthError(msg);
      setIsAuthenticating(false);
    }
  };

  const handleGoogleAuth = async () => {
    setAuthError('');
    setIsAuthenticating(true);
    try {
      if (isFirebaseConfigured) {
        const userProfile = await loginWithGoogle();
        onLoginSuccess(userProfile, false);
      } else {
        setAuthError('Firebase is not configured.');
        setIsAuthenticating(false);
      }
    } catch (err: any) {
      setAuthError(err.message);
      setIsAuthenticating(false);
    }
  };

  const [resetLink, setResetLink] = useState('');
  const [verificationLink, setVerificationLink] = useState('');

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    setAuthError('');
    setAuthSuccess('');
    setResetLink('');
    try {
      const result = await resetPassword(email);
      if (result.resetLink) {
        setResetLink(result.resetLink);
        setAuthSuccess('Click the button below to reset your password:');
      } else {
        setAuthSuccess('Password reset email sent! Check your inbox.');
      }
    } catch (err: any) {
      setAuthError(err.message || 'Failed to send reset email');
    } finally {
      setIsAuthenticating(false);
    }
  };

  // Render auth form content
  const renderAuthForm = () => {
    if (authMode === 'forgot') {
      return (
        <>
          <button
            onClick={() => { setAuthMode('login'); setAuthError(''); setAuthSuccess(''); }}
            className="ns-btn ns-btn-quiet ns-btn-xs -ml-3 mb-4"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back to sign in
          </button>
          <h2 id="auth-title" className="ns-t-dialog">Reset password</h2>
          <p className="text-sm text-ink-soft mt-1 mb-6">Enter your email and we'll send you a reset link.</p>
          <form onSubmit={handleForgotPassword} className="space-y-3">
            <input
              type="email"
              placeholder="Email"
              aria-label="Email"
              autoComplete="email"
              className="ns-input"
              value={email}
              onChange={e => { setEmail(e.target.value); setAuthError(''); setAuthSuccess(''); setResetLink(''); }}
              required
            />
            {authError && <p role="alert" className="text-danger-deep text-xs">{authError}</p>}
            {authSuccess && <p role="status" className="text-ok-deep text-xs">{authSuccess}</p>}
            {resetLink ? (
              <a
                href={resetLink}
                target="_blank"
                rel="noopener noreferrer"
                className="ns-btn ns-btn-lavender w-full"
              >
                Reset password
                <ArrowRight size={16} aria-hidden="true" />
              </a>
            ) : (
              <button type="submit" disabled={isAuthenticating} className="ns-btn ns-btn-primary w-full">
                {isAuthenticating ? <><Loader2 className="animate-spin" size={16} aria-hidden="true" /><span className="sr-only">Sending reset link</span></> : 'Send reset link'}
              </button>
            )}
          </form>
        </>
      );
    }

    return (
      <>
        {/* The welcome picture: Pip at the noodle stall (utils/sayings.ts) */}
        <img src={SAYINGS.welcome.picture} alt={SAYINGS.welcome.alt} className="w-full aspect-[16/9] object-cover rounded-2xl mb-5" decoding="async" draggable={false} />
        <h2 id="auth-title" className="ns-t-dialog text-center">
          {authMode === 'login' ? 'Welcome back' : 'Create account'}
        </h2>
        <p className="text-sm text-ink-soft text-center mt-1 mb-6">
          {authMode === 'login' ? 'Sign in to open your workspaces' : 'Free to start: one workspace, three nodes, no card'}
        </p>

        {/* Tab pill */}
        <div className="ns-seg flex p-1 mb-5">
          <button
            type="button"
            aria-pressed={authMode === 'login'}
            onClick={() => { setAuthMode('login'); setAuthError(''); }}
            className="ns-seg-item flex-1 py-2 text-sm"
          >
            Sign in
          </button>
          <button
            type="button"
            aria-pressed={authMode === 'signup'}
            onClick={() => { setAuthMode('signup'); setAuthError(''); }}
            className="ns-seg-item flex-1 py-2 text-sm"
          >
            Sign up
          </button>
        </div>

        <form onSubmit={handleEmailAuth} className="space-y-3">
          {authMode === 'signup' && (
            <input type="text" placeholder="Name" aria-label="Name" autoComplete="name" className="ns-input" value={name} onChange={e => setName(e.target.value)} required />
          )}
          <input type="email" placeholder="Email" aria-label="Email" autoComplete="email" className="ns-input" value={email} onChange={e => setEmail(e.target.value)} required />
          <input type="password" placeholder="Password" aria-label="Password" autoComplete={authMode === 'login' ? 'current-password' : 'new-password'} className="ns-input" value={password} onChange={e => setPassword(e.target.value)} required />
          {authMode === 'login' && (
            <button type="button" onClick={() => { setAuthMode('forgot'); setAuthError(''); }} className="ns-link text-xs">
              Forgot password?
            </button>
          )}
          {authError && <p role="alert" className="text-danger-deep text-xs">{authError}</p>}
          {authSuccess && authMode === 'signup' && <p role="status" className="text-ok-deep text-xs">{authSuccess}</p>}
          {verificationLink ? (
            <a href={verificationLink} target="_blank" rel="noopener noreferrer" className="ns-btn ns-btn-lavender w-full">
              <Check size={16} aria-hidden="true" />
              Verify email
              <ArrowRight size={16} aria-hidden="true" />
            </a>
          ) : (
            <button type="submit" disabled={isAuthenticating} className="ns-btn ns-btn-primary w-full">
              {isAuthenticating
                ? <><Loader2 className="animate-spin" size={16} aria-hidden="true" /><span className="sr-only">{authMode === 'login' ? 'Signing in' : 'Creating account'}</span></>
                : (authMode === 'login' ? 'Sign in' : 'Start free')}
            </button>
          )}
        </form>

        {isFirebaseConfigured && (
          <>
            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-cream-300" /></div>
              <div className="relative flex justify-center text-xs"><span className="px-3 bg-white text-ink-soft font-medium">or</span></div>
            </div>
            <button onClick={handleGoogleAuth} disabled={isAuthenticating} className="ns-btn ns-btn-ghost w-full">
              <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /* ui-ok: Google's logo keeps its own colours */ />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /* ui-ok: Google's logo keeps its own colours */ />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" /* ui-ok: Google's logo keeps its own colours */ />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /* ui-ok: Google's logo keeps its own colours */ />
              </svg>
              Continue with Google
            </button>
          </>
        )}

        {authMode === 'signup' && (
          <p className="mt-5 text-xs text-center text-ink-soft">
            Want unlimited workspaces & nodes?{' '}
            <button onClick={() => { handleCloseAuth(); onShowSubscriptionModal(); }} className="ns-link">
              See Pro
            </button>
          </p>
        )}
      </>
    );
  };

  /** Headlines are set in the brand's serif; the rest of the page in the UI's sans */
  const h2Class = 'font-brand font-normal text-[2rem] sm:text-[2.5rem] lg:text-[2.75rem] leading-[1.04] tracking-[-0.01em]';
  const eyebrowClass = 'text-2xs font-bold tracking-[0.14em] uppercase text-ink-soft';

  return (
    <div className="min-h-screen flex flex-col bg-cream text-ink overflow-x-clip">
      {/* ── Header: the page bar, held fixed (64px); <main> and the auth panel offset by the same token.
             The content column brings its own gutters, so the bar's are taken out. ── */}
      <header className="ns-pagebar fixed inset-x-0 z-50 px-0">
        <nav aria-label="Primary" className="ns-wrap w-full h-full flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 rounded-full shrink-0" aria-label="Noodle Studio home">
            <BrandMark size={32} />
            <span className="text-[1.0625rem] font-bold tracking-[-0.02em]">Noodle Studio</span>
            <BetaTag />
          </Link>

          <ul className="hidden md:flex items-center gap-1">
            <li><a href="#rooms" className="ns-nav-link">Tools</a></li>
            <li><a href="#keys" className="ns-nav-link">Keys</a></li>
            <li><a href="#pricing" className="ns-nav-link">Pricing</a></li>
            <li className="hidden lg:block"><a href="#beta" className="ns-nav-link">Beta</a></li>
          </ul>

          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => handleShowAuth('login')} className="ns-btn ns-btn-ghost ns-btn-sm hidden sm:inline-flex">
              Sign in
            </button>
            <button type="button" onClick={() => handleShowAuth('signup')} className="ns-btn ns-btn-primary ns-btn-sm">
              Open the studio
            </button>
          </div>
        </nav>
      </header>

      <main
        id="main"
        className="flex-1 pt-16"
        {...({ inert: showAuthPanel && isNarrow ? '' : undefined } as Record<string, unknown>)}
      >
        {/* ── Hero: shifts left beside the auth panel; sections below stay full width under it ── */}
        <section
          aria-labelledby="hero-title"
          className={`relative overflow-hidden pt-10 pb-14 sm:pt-14 sm:pb-16 lg:pt-20 lg:pb-24 [@media(max-height:720px)]:lg:pt-12 [@media(max-height:720px)]:lg:pb-14 transition-[padding] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            showAuthPanel ? 'md:pr-[50%] lg:pr-[45%]' : ''
          }`}
        >
          <div className="ns-wrap relative">
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10 lg:items-center">
              {/* Copy */}
              <div className={showAuthPanel ? 'lg:col-span-12' : 'lg:col-span-6'}>
                <p className={`${eyebrowClass} animate-fade-in-up`}>For writers, directors and producers</p>
                <h1
                  id="hero-title"
                  className="animate-fade-in-up mt-4 font-brand font-normal leading-[1.0] tracking-[-0.01em] text-[clamp(2.75rem,4.6vw,4.5rem)] text-balance"
                >
                  {TAGLINE}
                </h1>

                <p
                  className="animate-fade-in-up mt-5 text-[1.0625rem] sm:text-[1.125rem] lg:text-[1.1875rem] leading-[1.5] text-ink-soft max-w-[32rem] text-pretty"
                  style={{ animationDelay: '60ms' }}
                >
                  A studio where the story stays whole: ideas, script, cast, boards, shots and the cut, side by side. Bring your own
                  API keys for the models. We add nothing on top.
                </p>

                {/* CTA pair (collapses while the auth panel is open) */}
                <div
                  className={`flex flex-col sm:flex-row gap-3 transition-[opacity,transform] duration-300 ease-out ${
                    showAuthPanel ? 'opacity-0 scale-95 h-0 overflow-hidden pointer-events-none invisible mt-0' : 'opacity-100 scale-100 visible mt-8'
                  }`}
                >
                  <button type="button" onClick={() => handleShowAuth('signup')} className="ns-btn ns-btn-primary ns-btn-xl w-full sm:w-auto">
                    Open the studio
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                  <a href="#keys" className="ns-btn ns-btn-ghost ns-btn-xl w-full sm:w-auto">
                    How the keys work
                  </a>
                </div>

                <p className={`mt-5 text-[0.8125rem] font-medium text-ink-soft flex flex-wrap items-center gap-x-3 gap-y-1.5 ${showAuthPanel ? 'hidden' : ''}`}>
                  <BetaTag tone="lemon" />
                  <span>Free to start</span>
                  <span aria-hidden="true">·</span>
                  <span>Pro {PRO_MONTHLY_USDC} USDC a month</span>
                  <span aria-hidden="true">·</span>
                  <span>your keys stay yours</span>
                </p>

                {/* Feature pills, shown while the auth panel is open */}
                {showAuthPanel && (
                  <ul className="mt-8 space-y-3 animate-fade-in-up" aria-label="What you get">
                    {PANEL_PILLS.map(({ icon: Icon, tone, label }) => (
                      <li key={label} className="flex items-center gap-3 text-sm font-medium text-ink">
                        <span data-tone={tone} className={tone ? 'ns-mark ns-mark-sm' : 'ns-mark ns-mark-sm ns-mark-lavender'}>
                          <Icon aria-hidden="true" />
                        </span>
                        {label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* The studio, as it is (unmounted while the panel covers that region) */}
              {!showAuthPanel && (
                <div className="lg:col-span-6 w-full max-w-[560px] lg:max-w-none animate-fade-in-up" style={{ animationDelay: '180ms' }}>
                  <StudioWindow />
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── The six rooms ── */}
        <section id="rooms" aria-labelledby="rooms-title" className="scroll-mt-20 border-t border-cream-300 py-16 sm:py-20">
          <div className="ns-wrap">
            <p className={`${eyebrowClass} mb-3`}>One production, six rooms</p>
            <h2 id="rooms-title" className={`${h2Class} max-w-[46rem] mb-10 text-pretty`}>
              Every part of the story, in the room next door.
            </h2>
            <ul className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-5 gap-y-8" aria-label="The six rooms">
              {ROOMS.map(room => (
                <li key={room.name} className="flex flex-col gap-3">
                  <div className="h-[156px] flex items-end">{room.visual}</div>
                  <div>
                    <h3 className="text-[0.9375rem] font-bold">{room.name}</h3>
                    <p className="mt-0.5 text-[13px] leading-[1.45] text-ink-soft">{room.line}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── The credits: the maker's name on the story, ours on the tools ── */}
        <section aria-labelledby="credits-title" className="bg-ink text-cream py-16 sm:py-20 lg:py-24" data-credits>
          <div className="ns-wrap grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-12 items-center">
            <div className="lg:col-span-5">
              <p className={`${eyebrowClass} text-cream/60`}>How the credits read</p>
              <h2 id="credits-title" className="font-brand font-normal text-[3rem] sm:text-[3.75rem] lg:text-[4.5rem] leading-[1] tracking-[-0.01em] mt-3">Lantern Harbour</h2>
              <p className="font-brand italic text-[1.5rem] sm:text-[1.875rem] leading-tight text-cream/70 mt-1">a short film, made here</p>
              <p className="mt-7 text-[1.0625rem] leading-[1.55] text-cream/75 max-w-[28rem]">The maker’s name goes on the story. Ours goes on the tools. That is the whole arrangement.</p>
            </div>
            <div className="lg:col-span-7">
              <dl className="flex flex-col gap-2.5 max-w-[36rem]">
                {CREDITS.map(([role, who]) => (
                  <div key={role} className="flex items-baseline gap-3.5">
                    <dt className="w-[9.5rem] sm:w-[12.5rem] shrink-0 text-right text-2xs font-bold tracking-[0.14em] uppercase text-cream/60">{role}</dt>
                    <span className="flex-1 self-center h-px bg-cream/25" aria-hidden="true" />
                    <dd className="w-[11rem] sm:w-[18rem] shrink-0 font-brand text-[1.25rem] sm:text-[1.375rem] leading-tight">{who}</dd>
                  </div>
                ))}
              </dl>
              <ul className="mt-6 flex gap-2.5 sm:ml-[13.4rem]" aria-label="Three stills from the film">
                {STILLS.map(still => (
                  <li key={still.src}><img src={still.src} alt={still.alt} className="w-[120px] h-[68px] object-cover rounded-xl" loading="lazy" decoding="async" draggable={false} /></li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── Your keys ── */}
        <section id="keys" aria-labelledby="keys-title" className="scroll-mt-20 py-16 sm:py-20">
          <div className="ns-wrap">
            <p className={`${eyebrowClass} mb-3`}>Your keys</p>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <h2 id="keys-title" className={`${h2Class} text-pretty`}>Your keys. Your models. Your bill.</h2>
              <p className="text-[1rem] leading-[1.55] text-ink-soft max-w-[32rem]">Noodle Studio does not resell a single token. Add a key from Venice, Google or OpenAI in Settings; every ask shows what it will cost before you press it, and the cost stays with you.</p>
            </div>
            <div className="mt-9 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {KEY_PROVIDERS.map(provider => (
                <article key={provider.name} className="ns-tile flex flex-col gap-3">
                  <div className="flex items-center gap-2.5">
                    <span className="ns-mark ns-mark-lavender"><KeyRound aria-hidden="true" /></span>
                    <h3 className="text-[1rem] font-bold">{provider.name}</h3>
                  </div>
                  <p className="text-[0.875rem] leading-[1.5] text-ink-soft">{provider.line}</p>
                  <p aria-hidden="true" className="mt-auto flex items-center gap-2">
                    <span className="flex-1 h-9 rounded-full bg-cream-50 border border-cream-300 flex items-center px-3.5 ns-t-mono text-xs text-ink-soft tracking-[0.2em]">••••••••••••••</span>
                    <span className="ns-chip ns-chip-lavender">Replace</span>
                  </p>
                </article>
              ))}
              <div className="flex flex-col gap-3">
                <div className="ns-tile flex flex-col gap-2 py-4" aria-label="What you see before an ask">
                  <p className="text-xs font-bold">Before you press it</p>
                  <p className="flex items-center gap-2 text-[13px] text-ink-soft"><span className="ns-tone-dot bg-lavender" aria-hidden="true" />Up to about 6 cents · Claude Sonnet 5.5 · medium</p>
                  <p className="flex items-center gap-2 text-[13px] text-ink-soft"><span className="ns-tone-dot bg-mint" aria-hidden="true" />Balance 12.50 USD · Venice</p>
                </div>
                <p className="text-[13px] leading-[1.5] text-ink-soft px-1">Keys stay on your computer, encrypted for your account. We never see them.</p>
              </div>
            </div>
            <p className="mt-6 text-[13px] font-medium text-ink-soft flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-bold text-ink">In the studio today</span>
              {MODELS.map(model => <span key={model} className="ns-chip ns-chip-sm bg-white border border-cream-300 text-ink">{model}</span>)}
            </p>
          </div>
        </section>

        {/* ── Beta, said plainly ── */}
        <section id="beta" aria-labelledby="beta-title" className="scroll-mt-20 pb-16 sm:pb-20">
          <div className="ns-wrap grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
            <div className="lg:col-span-8 rounded-3xl bg-lemon-soft text-lemon-deep p-7 sm:p-9 flex flex-col">
              <p className={`${eyebrowClass} text-lemon-deep`}>Beta, said plainly</p>
              <h2 id="beta-title" className={`${h2Class} mt-3`}>The studio is a draft too.</h2>
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
                {BETA_POINTS.map(([head, text]) => (
                  <li key={head}>
                    <p className="text-[0.9375rem] font-bold">{head}</p>
                    <p className="mt-1 text-[0.875rem] leading-[1.5] opacity-85">{text}</p>
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                <Link to="/bugs" className="ns-btn ns-btn-primary">Tell us what broke</Link>
              </div>
            </div>
            <aside className="lg:col-span-4 ns-tile flex flex-col gap-3" aria-labelledby="new-title">
              <div className="flex items-center justify-between">
                <h3 id="new-title" className={eyebrowClass}>What landed lately</h3>
                <span className="text-xs text-ink-soft">October</span>
              </div>
              {WHATS_NEW.map(([head, text]) => (
                <div key={head} className="border-t border-cream-300 pt-3">
                  <p className="text-[0.875rem] font-bold">{head}</p>
                  <p className="mt-0.5 text-[13px] leading-[1.45] text-ink-soft">{text}</p>
                </div>
              ))}
            </aside>
          </div>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" aria-labelledby="pricing-title" className="scroll-mt-20 pb-16 sm:pb-20">
          <div className="ns-wrap">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
              <h2 id="pricing-title" className={h2Class}>Start free. Pay when it earns its place.</h2>
              <p className="text-[1rem] text-ink-soft max-w-[26rem]">No card to start. The models are paid through your own keys either way.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[56rem]">
              <article className="ns-tile flex flex-col gap-5" aria-labelledby="plan-free">
                <div>
                  <p id="plan-free" className="text-sm font-bold text-ink-soft">Free</p>
                  <p className="text-[2.5rem] leading-none font-bold tracking-[-0.03em] mt-1.5">$0</p>
                </div>
                <ul className="space-y-2.5 text-[0.9375rem] font-medium">
                  {FREE_PLAN.map(line => (
                    <li key={line} className="flex items-center gap-2.5"><Check size={16} className="text-ok-deep shrink-0" aria-hidden="true" />{line}</li>
                  ))}
                </ul>
                <button type="button" onClick={() => handleShowAuth('signup')} className="ns-btn ns-btn-primary mt-auto w-full">Open the studio</button>
              </article>
              <article className="ns-tile ns-tile-lavender flex flex-col gap-5" aria-labelledby="plan-pro">
                <div>
                  <p id="plan-pro" className="text-sm font-bold text-lavender-deep">Pro</p>
                  <p className="text-[2.5rem] leading-none font-bold tracking-[-0.03em] mt-1.5">${PRO_MONTHLY_USDC}<span className="text-lg font-semibold text-ink/50 line-through ml-2 align-middle">${PRO_LIST_USDC}</span></p>
                  <p className="text-[0.9375rem] leading-snug font-semibold mt-1.5 text-ink/80">a month, paid in USDC · {PRO_DISCOUNT_PERCENT}% off in beta</p>
                </div>
                <ul className="space-y-2.5 text-[0.9375rem] font-medium">
                  {PRO_PLAN.map(line => (
                    <li key={line} className="flex items-center gap-2.5"><Check size={16} className="text-lavender-deep shrink-0" aria-hidden="true" />{line}</li>
                  ))}
                </ul>
                <button type="button" onClick={onShowSubscriptionModal} className="ns-btn ns-btn-ghost mt-auto w-full bg-white/70">See Pro plans</button>
              </article>
            </div>
          </div>
        </section>

        {/* ── Closing ── */}
        <section aria-labelledby="cta-title" className="pb-16 sm:pb-20 lg:pb-24">
          <div className="ns-wrap">
            <div className="relative overflow-hidden rounded-3xl bg-ink text-cream p-8 sm:p-12 lg:p-14">
              <div className="relative grid lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <h2 id="cta-title" className={`${h2Class} text-pretty`}>
                    Make the thing you keep thinking about.
                  </h2>
                  <p className="mt-4 text-[1.0625rem] sm:text-[1.125rem] text-cream/70 max-w-[34rem]">
                    Sign in with Google or email. Nothing to install, nothing to pay until it earns its place.
                  </p>
                </div>
                <div className="lg:col-span-4 flex lg:justify-end">
                  <button type="button" onClick={() => handleShowAuth('signup')} className="ns-btn ns-btn-cream ns-btn-xl w-full sm:w-auto">
                    Open the studio
                    <ArrowRight size={16} aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Auth panel (slides in from the right; starts under the 64px header) ── */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={showAuthPanel && isNarrow ? 'true' : 'false'}
        aria-labelledby="auth-title"
        className={`fixed right-0 top-16 bottom-0 z-40 w-full md:w-1/2 lg:w-[45%] bg-white md:bg-white/95 backdrop-blur-md border-l border-cream-300 shadow-lift transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          showAuthPanel ? 'translate-x-0 opacity-100 visible' : 'translate-x-full opacity-0 invisible'
        }`}
      >
        <div className="h-full overflow-y-auto p-8 lg:p-12">
          <div className="max-w-md mx-auto">
            <button
              type="button"
              onClick={handleCloseAuth}
              className="ns-icon-btn ns-icon-btn-lg absolute top-6 right-6"
              aria-label="Close"
            >
              <X />
            </button>

            <div className={`transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${showAuthPanel ? 'opacity-100 translate-x-0 delay-200' : 'opacity-0 translate-x-8'}`}>
              {renderAuthForm()}
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <footer
        className={`mt-auto border-t border-cream-300 bg-cream/70 backdrop-blur-sm transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] shrink-0 relative z-10 ${
          showAuthPanel ? 'opacity-0 h-0 overflow-hidden invisible' : 'opacity-100 visible'
        }`}
      >
        <div className="ns-wrap py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-ink-soft text-xs font-medium">© 2026 Noodle Studio · Beta · Slurp responsibly.</p>
          <nav aria-label="Footer" className="flex items-center gap-1">
            <Link to="/bugs" className="ns-btn ns-btn-quiet ns-btn-xs">Known issues</Link>
            <Link to="/about" className="ns-btn ns-btn-quiet ns-btn-xs">About</Link>
            <Link to="/terms" className="ns-btn ns-btn-quiet ns-btn-xs">Terms</Link>
            <Link to="/privacy" className="ns-btn ns-btn-quiet ns-btn-xs">Privacy</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};
